import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { Callout } from '../../components/workbook/Callout';
import { getPhaseColor, getPhaseDescription } from '../../lib/workbook/utils';

const arrangePages = [
  { number: 1, title: 'Affinity Clusters', description: 'See the shape', duration: '10 min' },
  { number: 2, title: 'Pick ONE Comparison Method', description: 'Choose and commit', duration: '15 min' },
  { number: 3, title: 'Evidence Check', description: 'Sanity before ranking', duration: '10 min' },
  { number: 4, title: 'Portfolio Balance', description: 'Avoid all eggs in one basket', duration: '10 min' },
  { number: 5, title: 'Dependencies & Sequencing', description: 'Make constraints visible', duration: '10 min' },
  { number: 6, title: 'Ranked Shortlist', description: '≤ 7 items', duration: '10-20 min' },
  { number: 7, title: 'Play It Out', description: 'Gallery walk', duration: '10-15 min' },
  { number: 8, title: 'Facilitation Rails', description: 'Retrieval check', duration: '5 min' },
];

export default function ArrangePhase() {
  const phaseColor = getPhaseColor('arrange', 600);
  const phaseBg = getPhaseColor('arrange', 50);

  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: phaseBg, borderColor: phaseColor + '40' }}>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold" style={{ backgroundColor: phaseColor }}>
              A
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ color: phaseColor }}>Arrange</h1>
              <p style={{ color: phaseColor + 'DD' }}>{getPhaseDescription('arrange')}</p>
            </div>
          </div>
          <p className="max-w-3xl" style={{ color: phaseColor + 'CC' }}>
            Make sense of your options through clustering, comparison, and balance. 
            Create a ranked shortlist with clear rationale before making final choices.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Phase Overview */}
            <div className="bg-[var(--surface)] p-6">
              <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
                Phase Overview
              </h2>
              <div className="space-y-4">
                <p className="text-[var(--text-sec)]">
                  Organize, compare, and balance your options to create a clear shortlist. 
                  This phase helps you:
                </p>
                <ul className="space-y-2 text-[var(--text-sec)]">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Cluster related items into clear themes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Compare options with lightweight, visible methods</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Balance the portfolio (quick wins, big bets, sustaining, learning)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Publish a ranked shortlist with rationale and dependencies</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Pages List */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">
                Pages in This Phase
              </h2>
              {arrangePages.map((page) => (
                <Link
                  key={page.number}
                  to={`/elevation/workbook/arrange/page-${page.number}`}
                  className="block p-4 border border-[var(--rule)] hover:border-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors group"
                  style={{ borderColor: phaseColor + '40' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: phaseColor }}>
                        {page.number}
                      </div>
                      <div>
                        <h3 className="font-semibold text-[var(--text-pri)] group-hover:text-[var(--mustard)]">
                          {page.title}
                        </h3>
                        <p className="text-sm text-[var(--text-sec)]">
                          {page.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
                      <Clock className="w-4 h-4" />
                      {page.duration}
                      <ArrowRight className="w-4 h-4 group-hover:text-[var(--mustard)]" style={{ color: phaseColor }} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Key Concepts */}
            <Callout type="why" phase="arrange">
              <div className="space-y-2">
                <p className="font-semibold">Key Concepts:</p>
                <ul className="space-y-1 text-sm">
                  <li>• <strong>Clustering:</strong> See the shape before judging</li>
                  <li>• <strong>Comparison:</strong> Use simple, visible methods</li>
                  <li>• <strong>Balance:</strong> Mix quick wins with big bets</li>
                  <li>• <strong>Shortlist:</strong> Ranked list with clear rationale</li>
                </ul>
              </div>
            </Callout>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Start */}
            <div className="p-4 border" style={{ backgroundColor: phaseBg, borderColor: phaseColor + '40' }}>
              <h3 className="font-semibold mb-3" style={{ color: phaseColor + 'CC' }}>
                Quick Start
              </h3>
              <div className="space-y-2 text-sm" style={{ color: phaseColor + 'AA' }}>
                <p>1. Cluster items into themes</p>
                <p>2. Compare with chosen method</p>
                <p>3. Balance the portfolio</p>
                <p>4. Create ranked shortlist</p>
              </div>
            </div>

            {/* Timing Guide */}
            <div className="bg-[var(--surface)] border border-[var(--rule)] p-4">
              <h3 className="font-semibold text-[var(--text-pri)] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Timing Guide
              </h3>
              <div className="space-y-2 text-sm text-[var(--text-sec)]">
                <div className="flex justify-between">
                  <span>Solo work:</span>
                  <span className="font-medium">~80 min</span>
                </div>
                <div className="flex justify-between">
                  <span>Team session:</span>
                  <span className="font-medium">~120 min</span>
                </div>
                <div className="flex justify-between">
                  <span>Coach workshop:</span>
                  <span className="font-medium">~90 min</span>
                </div>
              </div>
            </div>

            {/* Success Criteria */}
            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
              <h3 className="font-semibold text-[var(--text-sec)] mb-3">
                Success Criteria
              </h3>
              <div className="space-y-2 text-sm text-[var(--text-sec)]">
                <p>✓ Items clustered into themes</p>
                <p>✓ Options compared with visible method</p>
                <p>✓ Portfolio balanced</p>
                <p>✓ Ranked shortlist published</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between">
          <Link
            to="/elevation/workbook/home"
            className="px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
          >
            ← Back to Overview
          </Link>
          <Link
            to="/elevation/workbook/arrange/page-1"
            className="inline-flex items-center gap-2 text-white px-6 py-3 font-semibold hover:opacity-90 transition-colors"
            style={{ backgroundColor: phaseColor }}
          >
            Start Arrange Phase
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

